import { useMemo } from 'react'
import { getFlavorsByCategory, type Flavor } from '../data'
import { getFlavorEmoji } from '../data/flavorMeta'

interface FlavorGridProps {
  search: string
  onSelect: (flavor: Flavor) => void
}

/** Склонение: 1 пара, 2 пары, 5 пар. */
const pairsLabel = (n: number) => {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return `${n} пара`
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${n} пары`
  return `${n} пар`
}

/**
 * Сетка вкусов, сгруппированная по книжным категориям. При активном поиске
 * категории схлопываются в плоский список подходящих вкусов.
 */
const FlavorGrid = ({ search, onSelect }: FlavorGridProps) => {
  const groups = useMemo(() => getFlavorsByCategory(), [])

  const query = search.trim().toLowerCase()

  const filteredGroups = useMemo(() => {
    if (!query) return groups
    return groups
      .map(g => ({
        ...g,
        flavors: g.flavors.filter(f => f.name.toLowerCase().includes(query)),
      }))
      .filter(g => g.flavors.length > 0)
  }, [groups, query])

  if (filteredGroups.length === 0) {
    return (
      <p className="flavors-empty">
        По запросу «{search}» вкусов не нашлось
      </p>
    )
  }

  return (
    <div className="flavors-groups">
      {filteredGroups.map(group => (
        <section key={group.category} className="flavors-group">
          <h2 className="flavors-group__title">{group.category}</h2>
          <div className="flavors-grid">
            {group.flavors.map(flavor => (
              <button
                key={flavor.slug}
                type="button"
                className="flavor-tile"
                onClick={() => onSelect(flavor)}
              >
                <span className="flavor-tile__row">
                  <span className="flavor-tile__emoji" aria-hidden="true">
                    {getFlavorEmoji(flavor.slug)}
                  </span>
                  <span className="flavor-tile__name">{flavor.name}</span>
                </span>
                <span className="flavor-tile__count">
                  {pairsLabel(flavor.pairings.length)}
                </span>
              </button>
            ))}
          </div>
        </section>
      ))}
    </div>
  )
}

export default FlavorGrid
